import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { endpoints } from '@/api/endpoints';
import { useApi } from '@/api/useApi';
import { useAuth } from '@/auth/AuthContext';
import { LoadingState } from '@/components/EmptyState';
import { ErrorBanner } from '@/components/ErrorBanner';
import { useLanguage } from '@/i18n/LanguageContext';
import { formatDateTime, formatNumber } from '@/i18n/format';
import { ZONE_PURPOSES, type CreateCellsResult, type WarehouseStructure, type WarehouseZone, type ZonePurpose } from '@/api/types';
import { PURPOSE_META, SYSTEM_KIND_META, describeSetupError, newKey } from './setup';
import { CellsBrowser } from './CellsBrowser';
import { LabelsPanel } from './LabelsPanel';

/**
 * ADR-083 — one warehouse's structure, in words rather than ids: the system
 * places every operation relies on, the zones (what each is FOR and what can
 * happen there), and their cells. Everything a form here can create is
 * something receiving/putaway/picking will accept - the options offered are
 * exactly the server's zone purposes, each mapped to an existing location kind.
 */
export function WarehouseDetailPage(): JSX.Element {
  const { id = '' } = useParams();
  const { t } = useLanguage();
  const { state, hasPermission } = useAuth();
  const result = useApi(() => endpoints.warehouseStructure(id), [id]);
  const scopeOk = state.status === 'authenticated' && (state.self.warehouse_id === null || state.self.warehouse_id === id);
  const canEdit = hasPermission('ORG.WRITE') && scopeOk;
  // Keep the last structure on screen while it refreshes after a change, so the
  // page never blanks into a spinner and the "N cells created" notes survive.
  const [data, setData] = useState<WarehouseStructure | null>(null);
  const fresh = result.status === 'success' ? result.data : null;
  useEffect(() => {
    if (fresh) setData(fresh);
  }, [fresh]);

  return (
    <div>
      <p className="breadcrumb">
        <Link to="/warehouses">← {t('nav.warehouses')}</Link>
      </p>
      {!data && result.status === 'loading' && <LoadingState />}
      {result.status === 'error' && <ErrorBanner error={result.error} onRetry={result.reload} />}
      {data && data.warehouse.id === id && <Structure data={data} canEdit={canEdit} reload={result.reload} />}
    </div>
  );
}

function Structure({ data, canEdit, reload }: { data: WarehouseStructure; canEdit: boolean; reload: () => void }): JSX.Element {
  const { t, locale } = useLanguage();
  const missing = data.systemLocations.filter((l) => l.code === null);
  const cellTotal = data.zones.reduce((s, z) => s + z.cell_count, 0);
  const ready = missing.length === 0 && data.zones.some((z) => z.cell_kind === 'BIN' && z.cell_count > 0);
  const [notice, setNotice] = useState<string | null>(null);
  const archived = data.archivedLocations ?? [];
  const { hasPermission } = useAuth();

  return (
    <>
      <div className="page-header">
        <div>
          <h1>
            {data.warehouse.name} <span className="badge neutral">{data.warehouse.code}</span>
          </h1>
          <div className="sub">
            {data.warehouse.legal_entity_name} · {data.warehouse.time_zone}
          </div>
        </div>
      </div>

      {notice && <p className="ok-note" role="status">{notice}</p>}
      <SetupSteps hasZones={data.zones.length > 0} hasCells={cellTotal > 0} ready={ready} />

      <section className="card" aria-labelledby="sys-title">
        <h2 id="sys-title" className="card-title">{t('warehouses.systemTitle')}</h2>
        <p className="field-hint">{t('warehouses.systemIntro')}</p>
        <ul className="place-list">
          {data.systemLocations.map((l) => (
            <li key={l.kind}>
              <div>
                <strong>{t(SYSTEM_KIND_META[l.kind].label)}</strong>
                {l.code && <span className="mono"> {l.code}</span>}
                <div className="field-hint">{t(SYSTEM_KIND_META[l.kind].help)}</div>
              </div>
              {l.code ? <span className="badge ok">{t('warehouses.present')}</span> : <span className="badge warn">{t('warehouses.missing')}</span>}
            </li>
          ))}
        </ul>
        {missing.length > 0 && canEdit && <CreateSystemLocations warehouseId={data.warehouse.id} onDone={reload} />}
      </section>

      <section className="card" aria-labelledby="zones-title">
        <h2 id="zones-title" className="card-title">{t('zones.title')}</h2>
        <p className="field-hint">{t('zones.intro')}</p>
        {data.zones.length === 0 && <p className="sub">{t('zones.none')}</p>}
        <div className="zone-list">
          {data.zones.map((z) => (
            <ZoneCard key={z.id} zone={z} warehouseId={data.warehouse.id} cells={data.cells.filter((c) => c.zone_id === z.id)} canEdit={canEdit} onChanged={reload} onNotice={setNotice} />
          ))}
        </div>
        {canEdit && <AddZoneForm warehouseId={data.warehouse.id} onCreated={reload} startOpen={data.zones.length === 0} />}
        {!canEdit && <p className="disabled-note">{t('zones.readOnly')}</p>}
      </section>

      {hasPermission('REFERENCE.READ') && (cellTotal > 0 || data.otherLocations.length > 0 || data.systemLocations.some((l) => l.id)) && <LabelsPanel data={data} />}

      {data.otherLocations.length > 0 && (
        <section className="card" aria-labelledby="other-title">
          <details>
            <summary>
              <h2 id="other-title" className="card-title inline">
                {t('warehouses.otherTitle', { n: formatNumber(data.otherLocations.length, locale) })}
              </h2>
            </summary>
            <p className="field-hint">{t('warehouses.otherIntro')}</p>
            <CellsBrowser warehouseId={data.warehouse.id} cells={data.otherLocations} canEdit={canEdit} onChanged={reload} onNotice={setNotice} />
          </details>
        </section>
      )}

      {archived.length > 0 && (
        <section className="card" aria-labelledby="archived-title">
          <details>
            <summary>
              <h2 id="archived-title" className="card-title inline">
                {t('cells.archivedTitle', { n: formatNumber(archived.length, locale) })}
              </h2>
            </summary>
            <p className="field-hint">{t('cells.archivedIntro')}</p>
            <ul className="place-list">
              {archived.map((l) => (
                <ArchivedRow key={l.id} warehouseId={data.warehouse.id} location={l} canEdit={canEdit} onChanged={reload} onNotice={setNotice} />
              ))}
            </ul>
          </details>
        </section>
      )}

      {ready && (
        <section className="card success-card" aria-labelledby="done-title">
          <h2 id="done-title" className="card-title">{t('warehouses.doneTitle')}</h2>
          <ol className="done-list">
            <li>{t('warehouses.done.receive')}</li>
            <li>{t('warehouses.done.putaway')}</li>
            <li>{t('warehouses.done.pick')}</li>
            <li>{t('warehouses.done.isolate')}</li>
            <li>{t('warehouses.done.tsd')}</li>
          </ol>
        </section>
      )}
    </>
  );
}

function SetupSteps({ hasZones, hasCells, ready }: { hasZones: boolean; hasCells: boolean; ready: boolean }): JSX.Element {
  const { t } = useLanguage();
  const steps: [string, boolean][] = [
    [t('warehouses.step.created'), true],
    [t('warehouses.step.zones'), hasZones],
    [t('warehouses.step.cells'), hasCells],
    [t('warehouses.step.ready'), ready],
  ];
  const current = steps.findIndex(([, done]) => !done);
  return (
    <ol className="wizard-steps" aria-label={t('warehouses.stepsLabel')}>
      {steps.map(([label, done], i) => (
        <li key={label} className={`step${done ? ' done' : ''}${i === current ? ' active' : ''}`} aria-current={i === current ? 'step' : undefined}>
          {label}
        </li>
      ))}
    </ol>
  );
}

function CreateSystemLocations({ warehouseId, onDone }: { warehouseId: string; onDone: () => void }): JSX.Element {
  const { t } = useLanguage();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  return (
    <div className="form-actions">
      <button
        type="button"
        className="primary"
        disabled={busy}
        onClick={() => {
          setBusy(true);
          setError(null);
          endpoints
            .createSystemLocations(warehouseId)
            .then(onDone, setError)
            .finally(() => setBusy(false));
        }}
      >
        {t('warehouses.createMissing')}
      </button>
      {error !== null && <div className="error-banner" role="alert">{describeSetupError(t, error) ?? t('warehouses.err.checkFields')}</div>}
    </div>
  );
}

function Capabilities({ purpose }: { purpose: ZonePurpose }): JSX.Element {
  const { t } = useLanguage();
  const m = PURPOSE_META[purpose];
  const item = (ok: boolean, key: 'receive' | 'store' | 'pick' | 'isolate') => (
    <li className={ok ? 'yes' : 'no'}>
      <span aria-hidden="true">{ok ? '✓' : '—'}</span> {t(`zones.can.${key}` as never)}
      <span className="sr-only">{ok ? t('zones.yes') : t('zones.no')}</span>
    </li>
  );
  return (
    <ul className="capabilities" aria-label={t('zones.capabilitiesLabel')}>
      {item(m.receive, 'receive')}
      {item(m.store, 'store')}
      {item(m.pick, 'pick')}
      {item(m.isolate, 'isolate')}
    </ul>
  );
}

function ArchivedRow({
  warehouseId,
  location,
  canEdit,
  onChanged,
  onNotice,
}: {
  warehouseId: string;
  location: { id: string; code: string; archived_at: string };
  canEdit: boolean;
  onChanged: () => void;
  onNotice: (message: string) => void;
}): JSX.Element {
  const { t, locale } = useLanguage();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  return (
    <li>
      <div>
        <span className="mono">{location.code}</span>
        <div className="field-hint">{t('cells.archivedOn', { date: formatDateTime(location.archived_at, locale) })}</div>
        {error !== null && <div className="error-banner" role="alert">{describeSetupError(t, error) ?? t('warehouses.err.checkFields')}</div>}
      </div>
      {canEdit && (
        <button
          type="button"
          disabled={busy}
          aria-label={t('cells.restoreAria', { code: location.code })}
          onClick={() => {
            setBusy(true);
            setError(null);
            endpoints
              .restoreLocation(warehouseId, location.id)
              .then(() => {
                onChanged();
                onNotice(t('cells.restoredDone', { code: location.code }));
              }, setError)
              .finally(() => setBusy(false));
          }}
        >
          {t('cells.restore')}
        </button>
      )}
    </li>
  );
}

function RenameZoneForm({ warehouseId, zone, onDone, onClose }: { warehouseId: string; zone: WarehouseZone; onDone: (name: string) => void; onClose: () => void }): JSX.Element {
  const { t } = useLanguage();
  const [name, setName] = useState(zone.name);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const errText = error ? describeSetupError(t, error) : null;
  const trimmed = name.trim();
  return (
    <form
      className="sub-form"
      aria-label={t('zones.renameFor', { zone: zone.name })}
      onSubmit={(e) => {
        e.preventDefault();
        setBusy(true);
        setError(null);
        endpoints
          .updateZone(warehouseId, zone.id, { name: trimmed })
          .then((r) => onDone(r.name), setError)
          .finally(() => setBusy(false));
      }}
    >
      <div className="field-row">
        <label htmlFor={`zone-rename-${zone.id}`}>{t('zones.name')}</label>
        <input id={`zone-rename-${zone.id}`} value={name} maxLength={80} required disabled={busy} aria-describedby={`zone-rename-hint-${zone.id}`} onChange={(e) => setName(e.target.value)} />
        <p id={`zone-rename-hint-${zone.id}`} className="field-hint">
          {t('zones.renameHint', { purpose: t(PURPOSE_META[zone.purpose].label), code: zone.code })}
        </p>
      </div>
      {error !== null && (errText ? <div className="error-banner" role="alert">{errText}</div> : <ErrorBanner error={error} />)}
      <div className="form-actions">
        <button type="submit" className="primary" disabled={busy || trimmed === '' || trimmed === zone.name}>
          {busy ? t('warehouses.creating') : t('common.save')}
        </button>
        <button type="button" onClick={onClose} disabled={busy}>{t('common.cancel')}</button>
      </div>
    </form>
  );
}

function ZoneCard({
  zone,
  warehouseId,
  cells,
  canEdit,
  onChanged,
  onNotice,
}: {
  zone: WarehouseZone;
  warehouseId: string;
  cells: WarehouseStructure['cells'];
  canEdit: boolean;
  onChanged: () => void;
  onNotice: (message: string) => void;
}): JSX.Element {
  const { t, locale } = useLanguage();
  const [adding, setAdding] = useState(zone.cell_count === 0 && canEdit);
  const [renaming, setRenaming] = useState(false);
  return (
    <article className="zone-card" aria-labelledby={`zone-${zone.id}`}>
      <header className="zone-head">
        <h3 id={`zone-${zone.id}`}>
          {zone.name} <span className="badge neutral">{zone.code}</span>
        </h3>
        <span className={`badge ${zone.cell_kind === 'QUARANTINE' ? 'warn' : 'ok'}`} title={t('zones.purposeLabel')}>{t(PURPOSE_META[zone.purpose].label)}</span>
      </header>
      {canEdit &&
        (renaming ? (
          <RenameZoneForm
            warehouseId={warehouseId}
            zone={zone}
            onClose={() => setRenaming(false)}
            onDone={(name) => {
              setRenaming(false);
              onNotice(t('zones.renamed', { name }));
              onChanged();
            }}
          />
        ) : (
          <div className="form-actions">
            <button type="button" onClick={() => setRenaming(true)} aria-label={t('zones.renameFor', { zone: zone.name })}>
              {t('zones.rename')}
            </button>
          </div>
        ))}
      <p className="field-hint">{t(PURPOSE_META[zone.purpose].help)}</p>
      <Capabilities purpose={zone.purpose} />
      <p className="sub">
        {t('zones.cellsSummary', { n: formatNumber(zone.cell_count, locale), withStock: formatNumber(zone.cells_with_stock, locale) })}
      </p>
      {cells.length > 0 && (
        <details>
          <summary>{t('zones.showCells')}</summary>
          <CellsBrowser warehouseId={warehouseId} cells={cells} canEdit={canEdit} onChanged={onChanged} onNotice={onNotice} />
        </details>
      )}
      {canEdit &&
        (adding ? (
          <AddCellsForm warehouseId={warehouseId} zone={zone} onDone={onChanged} onClose={() => setAdding(false)} />
        ) : (
          <div className="form-actions">
            <button type="button" onClick={() => setAdding(true)}>
              {t('zones.addCells')}
            </button>
          </div>
        ))}
    </article>
  );
}

const DEFAULT_NAME: Record<ZonePurpose, 'zones.defaultName.STORAGE' | 'zones.defaultName.FBS' | 'zones.defaultName.DEFECT' | 'zones.defaultName.QUARANTINE' | 'zones.defaultName.OTHER'> = {
  STORAGE: 'zones.defaultName.STORAGE',
  FBS: 'zones.defaultName.FBS',
  DEFECT: 'zones.defaultName.DEFECT',
  QUARANTINE: 'zones.defaultName.QUARANTINE',
  OTHER: 'zones.defaultName.OTHER',
};

function AddZoneForm({ warehouseId, onCreated, startOpen }: { warehouseId: string; onCreated: () => void; startOpen: boolean }): JSX.Element {
  const { t } = useLanguage();
  const [open, setOpen] = useState(startOpen);
  const [purpose, setPurpose] = useState<ZonePurpose>('STORAGE');
  const [name, setName] = useState('');
  const [nameTouched, setNameTouched] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const key = useRef(newKey());
  const effectiveName = nameTouched ? name : t(DEFAULT_NAME[purpose]);

  if (!open) {
    return (
      <div>
        {notice && <p className="ok-note" role="status">{notice}</p>}
        <div className="form-actions">
        <button type="button" className="primary" onClick={() => { setNotice(null); setOpen(true); }}>
          {t('zones.add')}
        </button>
        </div>
      </div>
    );
  }

  const errText = error ? describeSetupError(t, error) : null;
  return (
    <form
      className="sub-form"
      aria-labelledby="zone-add-title"
      onSubmit={(e) => {
        e.preventDefault();
        setBusy(true);
        setError(null);
        endpoints
          .createZone(warehouseId, { name: effectiveName.trim(), purpose, ...(code.trim() ? { code: code.trim().toUpperCase() } : {}), idempotencyKey: key.current })
          .then((r) => {
            key.current = newKey();
            setNotice(t('zones.created', { name: effectiveName.trim(), code: r.code }));
            setOpen(false);
            setName('');
            setNameTouched(false);
            setCode('');
            onCreated();
          }, setError)
          .finally(() => setBusy(false));
      }}
    >
      <h3 id="zone-add-title">{t('zones.addTitle')}</h3>
      <fieldset className="purpose-choice" disabled={busy}>
        <legend>{t('zones.purposeLabel')}</legend>
        {ZONE_PURPOSES.map((p) => (
          <label key={p} className={`purpose-option${purpose === p ? ' selected' : ''}`}>
            <input type="radio" name="purpose" value={p} checked={purpose === p} onChange={() => setPurpose(p)} />
            <span>
              <strong>{t(PURPOSE_META[p].label)}</strong>
              <span className="field-hint">{t(PURPOSE_META[p].help)}</span>
            </span>
          </label>
        ))}
      </fieldset>
      <div className="form-grid">
        <div className="field-row">
          <label htmlFor="zone-name">{t('zones.name')}</label>
          <input id="zone-name" value={effectiveName} maxLength={80} required disabled={busy} onChange={(e) => { setNameTouched(true); setName(e.target.value); }} />
        </div>
        <div className="field-row">
          <label htmlFor="zone-code">{t('zones.code')}</label>
          <input
            id="zone-code"
            value={code}
            maxLength={8}
            disabled={busy}
            autoCapitalize="characters"
            autoComplete="off"
            placeholder={t('zones.codeAuto')}
            aria-describedby="zone-code-hint"
            onChange={(e) => setCode(e.target.value.toUpperCase())}
          />
          <p id="zone-code-hint" className="field-hint">{t('zones.codeHint')}</p>
        </div>
      </div>
      {error !== null && (errText ? <div className="error-banner" role="alert">{errText}</div> : <ErrorBanner error={error} />)}
      <div className="form-actions">
        <button type="submit" className="primary" disabled={busy || effectiveName.trim() === ''}>
          {busy ? t('warehouses.creating') : t('zones.addSubmit')}
        </button>
        <button type="button" onClick={() => setOpen(false)} disabled={busy}>
          {t('common.cancel')}
        </button>
      </div>
    </form>
  );
}

function cellPreview(zoneCode: string, row: string, rf: number, rt: number, lf: number, lt: number): { count: number; first: string; last: string } | null {
  if (!/^[A-Z0-9]{1,3}$/.test(row) || ![rf, rt, lf, lt].every(Number.isInteger) || rf < 1 || rt > 99 || rf > rt || lf < 1 || lt > 20 || lf > lt) return null;
  const pad = (n: number) => String(n).padStart(2, '0');
  return { count: (rt - rf + 1) * (lt - lf + 1), first: `${zoneCode}-${row}-${pad(rf)}-${lf}`, last: `${zoneCode}-${row}-${pad(rt)}-${lt}` };
}

function AddCellsForm({ warehouseId, zone, onDone, onClose }: { warehouseId: string; zone: WarehouseZone; onDone: () => void; onClose: () => void }): JSX.Element {
  const { t, locale } = useLanguage();
  const [row, setRow] = useState('A');
  const [rack, setRack] = useState({ from: '1', to: '10' });
  const [level, setLevel] = useState({ from: '1', to: '4' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [done, setDone] = useState<CreateCellsResult | null>(null);
  const num = (s: string) => (s.trim() === '' ? NaN : Number(s));
  const preview = cellPreview(zone.code, row.trim().toUpperCase(), num(rack.from), num(rack.to), num(level.from), num(level.to));
  const tooMany = preview !== null && preview.count > 500;
  const idp = `cells-${zone.id}`;

  const errText = error ? describeSetupError(t, error) : null;
  return (
    <form
      className="sub-form"
      aria-label={t('zones.addCellsFor', { zone: zone.name })}
      onSubmit={(e) => {
        e.preventDefault();
        if (!preview || tooMany) return;
        setBusy(true);
        setError(null);
        setDone(null);
        endpoints
          .createCells(warehouseId, zone.id, { row: row.trim().toUpperCase(), rackFrom: num(rack.from), rackTo: num(rack.to), levelFrom: num(level.from), levelTo: num(level.to) })
          .then((r) => {
            setDone(r);
            onDone();
          }, setError)
          .finally(() => setBusy(false));
      }}
    >
      <p className="field-hint">{t('zones.cellsExplain')}</p>
      <div className="range-grid">
        <div className="field-row">
          <label htmlFor={`${idp}-row`}>{t('zones.row')}</label>
          <input id={`${idp}-row`} value={row} maxLength={3} autoCapitalize="characters" disabled={busy} onChange={(e) => setRow(e.target.value.toUpperCase())} />
        </div>
        {([
          ['racks', rack, setRack, 99, 'zones.racks', 'zones.racksFrom', 'zones.racksTo'],
          ['levels', level, setLevel, 20, 'zones.levels', 'zones.levelsFrom', 'zones.levelsTo'],
        ] as const).map(([k, v, set, max, legend, fromName, toName]) => (
          <fieldset key={k} className="range-pair" disabled={busy}>
            <legend>{t(legend)}</legend>
            <label htmlFor={`${idp}-${k}-from`}>{t('zones.from')}</label>
            <input id={`${idp}-${k}-from`} aria-label={t(fromName)} type="number" inputMode="numeric" min={1} max={max} value={v.from} onChange={(e) => set({ ...v, from: e.target.value })} />
            <label htmlFor={`${idp}-${k}-to`}>{t('zones.to')}</label>
            <input id={`${idp}-${k}-to`} aria-label={t(toName)} type="number" inputMode="numeric" min={1} max={max} value={v.to} onChange={(e) => set({ ...v, to: e.target.value })} />
          </fieldset>
        ))}
      </div>
      <p className={`preview-line${preview && !tooMany ? '' : ' warn'}`} aria-live="polite">
        {preview === null
          ? t('zones.err.range', { max: '500' })
          : tooMany
            ? t('zones.tooMany', { n: formatNumber(preview.count, locale) })
            : t('zones.preview', { n: formatNumber(preview.count, locale), first: preview.first, last: preview.last })}
      </p>
      {done && (
        <p className="ok-note" role="status">
          {done.alreadyExisted > 0
            ? t('zones.cellsCreatedSome', { n: formatNumber(done.created, locale), existed: formatNumber(done.alreadyExisted, locale) })
            : t('zones.cellsCreated', { n: formatNumber(done.created, locale) })}
        </p>
      )}
      {error !== null && (errText ? <div className="error-banner" role="alert">{errText}</div> : <ErrorBanner error={error} />)}
      <div className="form-actions">
        <button type="submit" className="primary" disabled={busy || !preview || tooMany}>
          {busy ? t('warehouses.creating') : t('zones.createCells')}
        </button>
        <button type="button" onClick={onClose} disabled={busy}>
          {t('zones.close')}
        </button>
      </div>
    </form>
  );
}
